import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

// A quoted reply chain in an email is either a "container" (a single element that already holds
// the whole history, e.g. Gmail's .gmail_quote or Apple's blockquote[type="cite"]) or a "splitter"
// (a delimiter element such as Outlook's #divRplyFwdMsg, with the quoted body following it as
// siblings). We detect the boundary and wrap it in a native <details> so it collapses inside the
// scriptless email iframe with no JavaScript — the browser's built-in <details> toggle is enough.
type QuoteBoundaryKind = 'container' | 'splitter';

interface QuoteBoundary {
  element: Element;
  kind: QuoteBoundaryKind;
}

const QUOTE_DETAILS_CLASS = 'omnetic-quote';
const SUMMARY_SYMBOL = '···';

// Outlook renders the reply separator as a bordered <div>. DOMPurify keeps the style attribute
// verbatim, so we match the known signatures on the raw (space-stripped, lower-cased) style string
// rather than el.style.* — inline-style reads are normalized differently across browsers/jsdom.
// Each signature is a set of tokens that must ALL be present. The Windows Mail signature requires
// its full property set on purpose: rgb(229,229,229) is an extremely common hairline-divider color,
// so matching it in isolation would misclassify ordinary content as a quote splitter and hide it.
const OUTLOOK_SPLITTER_STYLE_SIGNATURES = [
  ['border-top:solid#b5c4df'], // Outlook 2007+
  ['border-top:solid#e1e1e1'], // Outlook for Android
  [
    'padding-top:5px',
    'border-top-color:rgb(229,229,229)',
    'border-top-width:1px',
    'border-top-style:solid',
  ], // Windows Mail
];

const SPLITTER_ID_SELECTORS = ['#divRplyFwdMsg', '#appendonsend'];
const SPLITTER_CLASS_SELECTORS = ['.moz-cite-prefix'];

const HIDDEN_STYLE_SIGNATURES = ['display:none', 'visibility:hidden'];

const normalizeStyle = (element: Element) =>
  (element.getAttribute('style') ?? '').replace(/\s+/g, '').toLowerCase();

const isOutlookSplitterDiv = (element: Element) => {
  const style = normalizeStyle(element);
  return OUTLOOK_SPLITTER_STYLE_SIGNATURES.some((tokens) =>
    tokens.every((token) => style.includes(token))
  );
};

// Hidden preheader/preview divs (display:none) carry inbox-preview text that is never rendered.
// They must not count as "content before the boundary", or an all-quote email that opens with a
// hidden preheader would collapse into a card showing only the toggle.
const isHidden = (element: Element) =>
  element.hasAttribute('hidden') ||
  HIDDEN_STYLE_SIGNATURES.some((signature) => normalizeStyle(element).includes(signature));

// Elements not contained by any other element in the same set — the outermost matches. Preserves
// document order (querySelectorAll order), so [0] is the first and at(-1) the last top-level match.
const topLevel = (elements: Element[]) =>
  elements.filter(
    (element) => !elements.some((other) => other !== element && other.contains(element))
  );

const queryAll = (root: ParentNode, selector: string) =>
  Array.from(root.querySelectorAll(selector));

const last = (elements: Element[]): Element | undefined => elements[elements.length - 1];

const earliestInDocument = (elements: Element[]) =>
  elements.reduce<Element | null>((earliest, element) => {
    if (isNil(earliest)) {
      return element;
    }
    const isBeforeEarliest =
      (element.compareDocumentPosition(earliest) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
    return isBeforeEarliest ? element : earliest;
  }, null);

// Detectors are evaluated in priority order; the first that returns a boundary wins. Specific,
// unambiguous markers (Gmail, Outlook splitter) come before the generic trailing <blockquote>.
const detectors: Array<(body: HTMLElement) => QuoteBoundary | null> = [
  (body) => {
    const element = topLevel(queryAll(body, '.gmail_quote'))[0];
    return isNotNil(element) ? {element, kind: 'container'} : null;
  },
  (body) => {
    const byId = SPLITTER_ID_SELECTORS.flatMap((selector) => queryAll(body, selector));
    const byStyle = queryAll(body, 'div').filter(isOutlookSplitterDiv);
    const element = earliestInDocument(topLevel([...byId, ...byStyle]));
    return isNotNil(element) ? {element, kind: 'splitter'} : null;
  },
  (body) => {
    const element = SPLITTER_CLASS_SELECTORS.flatMap((selector) => queryAll(body, selector))[0];
    return isNotNil(element) ? {element, kind: 'splitter'} : null;
  },
  (body) => {
    const element = topLevel(queryAll(body, '.yahoo_quoted, .protonmail_quote'))[0];
    return isNotNil(element) ? {element, kind: 'container'} : null;
  },
  (body) => {
    const element = last(topLevel(queryAll(body, 'blockquote[type="cite"]')));
    return isNotNil(element) ? {element, kind: 'container'} : null;
  },
  (body) => {
    const bare = queryAll(body, 'blockquote:not([type="cite"])').filter(
      (element) => !element.classList.contains('gmail_quote')
    );
    const element = last(topLevel(bare));
    return isNotNil(element) ? {element, kind: 'container'} : null;
  },
];

const findBoundary = (body: HTMLElement) =>
  detectors.reduce<QuoteBoundary | null>((found, detect) => found ?? detect(body), null);

// True when there is visible reply text before the boundary. Guards against collapsing an email
// that is ENTIRELY quoted (a pure forward) — that would leave a card showing only the toggle.
const hasContentBefore = (body: HTMLElement, boundary: Element) => {
  const range = body.ownerDocument.createRange();
  range.setStart(body, 0);
  range.setEndBefore(boundary);
  const fragment = range.cloneContents();
  Array.from(fragment.querySelectorAll('*'))
    .filter(isHidden)
    .forEach((element) => element.remove());
  return (fragment.textContent ?? '').trim() !== '';
};

const collectBoundaryAndFollowing = (boundary: Element): ChildNode[] => {
  const nodes: ChildNode[] = [];
  let node: ChildNode | null = boundary;
  while (isNotNil(node)) {
    nodes.push(node);
    node = node.nextSibling;
  }
  return nodes;
};

const wrapBoundary = (doc: Document, boundary: QuoteBoundary, quotedLabel: string): boolean => {
  const parent = boundary.element.parentElement;
  if (isNil(parent)) {
    return false;
  }

  const nodesToMove: ChildNode[] = match(boundary.kind)
    .with('container', () => [boundary.element])
    .otherwise(() => collectBoundaryAndFollowing(boundary.element));

  const details = doc.createElement('details');
  details.className = QUOTE_DETAILS_CLASS;
  const summary = doc.createElement('summary');
  summary.setAttribute('aria-label', quotedLabel);
  summary.textContent = SUMMARY_SYMBOL;
  details.appendChild(summary);

  parent.insertBefore(details, boundary.element);
  nodesToMove.forEach((node) => details.appendChild(node));

  return true;
};

/**
 * Wraps an email's quoted reply chain in a collapsed native <details> toggle. Input MUST be already
 * sanitized (the caller runs DOMPurify first). Returns the input unchanged when no quoted chain is
 * found or when the whole email is quoted.
 */
export function collapseQuotedReplies(bodyHtml: string, quotedLabel: string): string {
  const doc = new DOMParser().parseFromString(bodyHtml, 'text/html');
  const body = doc.body;

  const boundary = findBoundary(body);
  if (isNil(boundary) || !hasContentBefore(body, boundary.element)) {
    return bodyHtml;
  }

  const wrapped = wrapBoundary(doc, boundary, quotedLabel);
  // A body fragment that leads with a head-hoistable element (<style>, <meta>, <link>, <base>,
  // <title>) has that element parsed into doc.head, not doc.body. Prepend it so re-serializing does
  // not silently drop the email's own <style> (which would break its rendering on the collapse path).
  return wrapped ? doc.head.innerHTML + body.innerHTML : bodyHtml;
}
